/**
 * WHAT MAY REACH GOOGLE: THE PAGE FIELDS (C60, R48; analytics spec, *What may never reach Google: the scrub*). This
 * module computes the three fields every entry of the data layer carries, and NOTHING ELSE in the web app (and nothing
 * in the container) reads the address for analytics.
 *
 *   gw_page_location  the origin of an allowed host plus the ROUTE TEMPLATE the path matches, from the table below.
 *                     A parameter is its literal name (`:code`), never its value. A path that matches no row is
 *                     `/not-found`, never the typed path (a person can type an email address into the address bar).
 *                     The fragment is always dropped. The query is dropped, except a campaign tag that passes every
 *                     rule of `campaigns.ts`, which the loader appends to the first page view after a grant.
 *   gw_page_referrer  empty for none, for a sign-in provider's host and for Groundwork's own /api/identity/ (the
 *                     round trip must not read as a new traffic source); a page of ours as its template; any other
 *                     as its ORIGIN only. After the first page of a page life, the previous page's location.
 *   gw_page_title     the route's name from the same table. `document.title` is never read: a later title could
 *                     carry a name.
 *
 * THE TABLE HOLDS EVERY PATH OF `frontend/src/router.tsx`, and `scrub.test.ts` fails on a route with no row, so a new
 * route cannot ship with its real address going to Google. It matches with the router's own `matchPath`, on the path
 * decoded as the router decodes it, so a row is the page the router renders: case-insensitive, any number of trailing
 * slashes, percent-escapes read. The loader decides a credential address from the same row.
 */
import { matchPath } from 'react-router-dom'

export interface PageFields {
  gw_page_location: string
  gw_page_referrer: string
  gw_page_title: string
}

export interface Route {
  /** The route's path as `router.tsx` spells it in full, its parameters by name. */
  template: string
  title: string
}

/** Every page of the web app (router.tsx), and one planned. */
export const ROUTES: readonly Route[] = [
  { template: '/', title: 'Dashboard' },
  { template: '/coach', title: 'Coach' },
  { template: '/dashboard', title: 'Dashboard' },
  { template: '/sessions', title: 'Sessions' },
  { template: '/builder', title: 'Session Builder' },
  { template: '/builder/:templateId', title: 'Session' },
  { template: '/progress', title: 'Journey' },
  { template: '/body', title: 'Journey' },
  { template: '/journey', title: 'Journey' },
  { template: '/profile', title: 'Profile' },
  { template: '/settings', title: 'Settings' },
  { template: '/knowledge-base', title: 'Knowledge Base' },
  { template: '/catalog-setup', title: 'Catalog setup' },
  { template: '/login', title: 'Sign in' },
  { template: '/signed-in', title: 'Signed in' },
  { template: '/invite/:code', title: 'Invitation' },
  { template: '/first-run', title: 'First run' },
  // A creator's link (#418, not built): the one page with a code in its path where the container is injected. The
  // code never leaves (here a template, and `campaigns.ts` drops a label that contains it).
  { template: '/i/:code', title: 'Creator link' },
]

/** Rows that are no route of `router.tsx` yet, by name, so the router-coverage test can tell them from a stale row. */
export const PLANNED_ROUTES: readonly string[] = ['/i/:code']

export const NOT_FOUND: Route = { template: '/not-found', title: 'Not found' }

/**
 * The landing site's own pages on the root domain (its `/` is the app's `/` as a template), known only so that a
 * referrer from one is reported as its template.
 */
const LANDING_TEMPLATES: readonly string[] = ['/waitlist/confirm', '/terms.html', '/privacy.html']

/** Providers the sign-in round trip goes through: a referrer from one is empty. */
const SIGN_IN_HOSTS: ReadonlySet<string> = new Set(['appleid.apple.com', 'accounts.google.com'])
/** Groundwork's own hosts: a referrer from one is read as a page of ours. */
const OWN_HOSTS: ReadonlySet<string> = new Set(['groundwork-sports.com', 'www.groundwork-sports.com', 'app.groundwork-sports.com'])

/**
 * The path as the router reads it before it matches (React Router's `decodePath`): every segment percent-decoded, a
 * decoded slash kept as `%2F`, and the path as it is when a segment cannot be decoded. Without this, `/%69nvite/CODE`
 * would be no row here while the router renders the invite page for it.
 */
export function routerPath(pathname: string): string {
  try {
    return pathname
      .split('/')
      .map((segment) => decodeURIComponent(segment).replace(/\//g, '%2F'))
      .join('/')
  } catch {
    return pathname
  }
}

/** A template matched the way the router matches a route: case-insensitive, to the end, any number of trailing slashes. */
const matchTemplate = (template: string, path: string) => matchPath({ path: template, caseSensitive: false, end: true }, path)

/** The row a path matches, and the match, or null. */
function find(pathname: string): { route: Route; params: Record<string, string | undefined> } | null {
  const path = routerPath(pathname)
  for (const route of ROUTES) {
    const found = matchTemplate(route.template, path)
    if (found) return { route, params: found.params }
  }
  return null
}

/** The row a path matches, or `/not-found`. Never the path itself. */
export function routeFor(pathname: string): Route {
  return find(pathname)?.route ?? NOT_FOUND
}

/** The code in a path of a route with a `:code` parameter (decoded, as the page reads it), for the campaign rule. */
export function codeIn(pathname: string): string | null {
  return find(pathname)?.params.code || null
}

/** The origin a page field is built on: an allowed host's, never the address's own (a port, a user part). */
export function originOf(hostname: string): string {
  return `https://${hostname}`
}

/** The page fields of a page of the web app: its template, the referrer given, its name. */
export function pageFields(hostname: string, pathname: string, referrer: string): PageFields {
  const route = routeFor(pathname)
  return {
    gw_page_location: originOf(hostname) + route.template,
    gw_page_referrer: referrer,
    gw_page_title: route.title,
  }
}

/** A referrer reduced to what may be sent: nothing, a page of ours as its template, or another site's origin. */
export function scrubReferrer(referrer: string): string {
  if (!referrer) return ''
  let url: URL
  try {
    url = new URL(referrer)
  } catch {
    return ''
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return ''
  const host = url.hostname.toLowerCase()
  if (SIGN_IN_HOSTS.has(host)) return ''
  if (!OWN_HOSTS.has(host)) return url.origin
  const path = routerPath(url.pathname)
  if (/^\/+api\/+identity(?:\/|$)/i.test(path)) return ''
  const template = [...ROUTES.map((route) => route.template), ...LANDING_TEMPLATES].find((t) => matchTemplate(t, path))
  return `${url.protocol}//${host}${template ?? ''}`
}
